import React, { useState, useEffect, useCallback } from 'react';
import { useData } from '../../contexts/DataContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell } from 'recharts';
import { Package, TrendingUp, TrendingDown, DollarSign, RefreshCw, Download, Calendar, Wrench, User, FileText, ChevronDown, ChevronRight, Filter, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';

const COLORS = ['#0052CC', '#FF5630', '#36B37E', '#6554C0', '#FF8B00', '#00B8D9', '#8777D9', '#57D9A3', '#FFC400', '#FF7452'];

const TicketConsumptionPage = () => {
  const { selectedPlant, plants, equipment: allEquipment } = useData();
  const [data, setData] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const [technicians, setTechnicians] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [activeTab, setActiveTab] = useState('detailed');
  
  // Filters
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [selectedEquipment, setSelectedEquipment] = useState('');
  const [selectedTechnician, setSelectedTechnician] = useState('');
  
  // Detail view
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchTechnicians();
    fetchEquipment();
  }, []);

  const fetchTechnicians = async () => {
    try {
      const res = await api.get('/users');
      setTechnicians(res.data.filter(u => ['technician', 'manager', 'admin'].includes(u.role)));
    } catch { /* ignore */ }
  };

  const fetchEquipment = async () => {
    try {
      const res = await api.get('/equipment');
      setEquipment(res.data);
    } catch { /* ignore */ }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        start_date: startDate,
        end_date: endDate
      };
      if (selectedPlant && selectedPlant !== 'all') params.plant_id = selectedPlant;
      if (selectedEquipment) params.equipment_id = selectedEquipment;
      if (selectedTechnician) params.technician_id = selectedTechnician;
      
      const res = await api.get('/reports/parts-consumption/tickets', { params });
      setData(res.data);
    } catch {
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedPlant, selectedEquipment, selectedTechnician]);

  const fetchComparisonData = useCallback(async () => {
    try {
      setLoadingComparison(true);
      const params = {};
      if (selectedPlant && selectedPlant !== 'all') params.plant_id = selectedPlant;
      
      const res = await api.get('/reports/parts-consumption/comparison', { params });
      setComparisonData(res.data);
    } catch {
      toast.error('Eroare la incarcarea datelor comparative');
    } finally {
      setLoadingComparison(false);
    }
  }, [selectedPlant]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (activeTab === 'comparison') fetchComparisonData(); }, [activeTab, fetchComparisonData]);

  const exportCSV = () => {
    if (!data) return;
    const rows = [['Data', 'Tichet', 'Echipament', 'Tehnician', 'Articol', 'Cantitate', 'UM', 'Valoare']];
    data.ticket_details.forEach(t => {
      t.items.forEach(item => {
        rows.push([
          t.closed_at?.split('T')[0] || '',
          t.ticket_title,
          t.equipment_name,
          t.closed_by_name,
          item.article_name,
          item.quantity,
          item.unit,
          item.value.toFixed(2)
        ]);
      });
    });
    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `consum_tichete_${startDate}_${endDate}.csv`;
    link.click();
  };

  const clearFilters = () => {
    setSelectedEquipment('');
    setSelectedTechnician('');
    const d = new Date();
    d.setDate(d.getDate() - 30);
    setStartDate(d.toISOString().split('T')[0]);
    setEndDate(new Date().toISOString().split('T')[0]);
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-12 h-12 animate-spin text-blue-600" />
      </div>
    );
  }

  const pieData = data?.top_articles?.slice(0, 8).map((a, i) => ({
    name: a.article_name.length > 18 ? a.article_name.substring(0, 18) + '...' : a.article_name,
    value: Math.round(a.quantity)
  })) || [];

  return (
    <div className="space-y-6" data-testid="ticket-consumption-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Consum Materiale din Tichete</h1>
          <p className="text-slate-500 mt-1">Analiza materialelor consumate la inchiderea tichetelor</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters-btn">
            <Filter className="w-4 h-4 mr-1" />
            Filtre
            {showFilters ? <ChevronDown className="w-4 h-4 ml-1" /> : <ChevronRight className="w-4 h-4 ml-1" />}
          </Button>
          <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-1" /> CSV
          </Button>
          <Button size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Actualizeaza
          </Button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="pt-4">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              <div className="space-y-1">
                <Label className="text-xs">Data start</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="h-9"
                  data-testid="start-date-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Data sfarsit</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="h-9"
                  data-testid="end-date-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Echipament</Label>
                <Select value={selectedEquipment} onValueChange={setSelectedEquipment}>
                  <SelectTrigger className="h-9" data-testid="equipment-filter">
                    <SelectValue placeholder="Toate" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Toate echipamentele</SelectItem>
                    {equipment.map(eq => (
                      <SelectItem key={eq.id} value={eq.id}>{eq.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Tehnician</Label>
                <Select value={selectedTechnician} onValueChange={setSelectedTechnician}>
                  <SelectTrigger className="h-9" data-testid="technician-filter">
                    <SelectValue placeholder="Toti" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Toti tehnicienii</SelectItem>
                    {technicians.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button variant="ghost" size="sm" onClick={clearFilters}>Reseteaza</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs for detailed vs comparison */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="detailed" data-testid="tab-detailed">Raport Detaliat</TabsTrigger>
          <TabsTrigger value="comparison" data-testid="tab-comparison">Comparatie Lunara</TabsTrigger>
        </TabsList>

        <TabsContent value="detailed" className="space-y-6 mt-4">
          {/* Summary KPIs */}
          {data && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <FileText className="w-6 h-6 mx-auto text-blue-500 mb-1" />
                <p className="text-2xl font-bold text-slate-900">{data.summary.total_tickets}</p>
                <p className="text-xs text-slate-500">Tichete inchise</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Package className="w-6 h-6 mx-auto text-purple-500 mb-1" />
                <p className="text-2xl font-bold text-slate-900">{data.summary.total_quantity.toLocaleString('ro-RO')}</p>
                <p className="text-xs text-slate-500">Cantitate totala</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <DollarSign className="w-6 h-6 mx-auto text-green-500 mb-1" />
                <p className="text-2xl font-bold text-slate-900">{data.summary.total_value.toLocaleString('ro-RO')}</p>
                <p className="text-xs text-slate-500">Valoare totala (RON)</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <TrendingUp className="w-6 h-6 mx-auto text-orange-500 mb-1" />
                <p className="text-2xl font-bold text-slate-900">{data.summary.unique_articles}</p>
                <p className="text-xs text-slate-500">Articole diferite</p>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Daily Trend */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Trend Zilnic Consum</CardTitle></CardHeader>
              <CardContent>
                {data.daily_trend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={data.daily_trend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(val) => val.toLocaleString('ro-RO')} labelFormatter={(l) => `Data: ${l}`} />
                      <Legend />
                      <Area type="monotone" dataKey="quantity" name="Cantitate" stroke="#0052CC" fill="#0052CC" fillOpacity={0.15} />
                      <Area type="monotone" dataKey="tickets" name="Tichete" stroke="#36B37E" fill="#36B37E" fillOpacity={0.1} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-slate-400 text-center py-12">Niciun consum in perioada selectata</p>
                )}
              </CardContent>
            </Card>

            {/* Top Articles Pie */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Top Articole Consumate</CardTitle></CardHeader>
              <CardContent>
                {pieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" outerRadius={95} fill="#0052CC" dataKey="value" label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`} labelLine={false}>
                        {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(val) => val.toLocaleString('ro-RO')} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-slate-400 text-center py-12">Niciun articol consumat</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* By Equipment and By Technician */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* By Equipment */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Wrench className="w-4 h-4" /> Consum per Echipament</CardTitle></CardHeader>
              <CardContent>
                {data.by_equipment.length > 0 ? (
                  <ResponsiveContainer width="100%" height={Math.max(180, data.by_equipment.length * 32)}>
                    <BarChart layout="vertical" data={data.by_equipment.slice(0, 10)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tick={{ fontSize: 10 }} />
                      <YAxis dataKey="equipment_name" type="category" width={140} tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(val) => val.toLocaleString('ro-RO')} />
                      <Bar dataKey="quantity" name="Cantitate" fill="#0052CC" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-slate-400 text-center py-8">Niciun echipament</p>
                )}
              </CardContent>
            </Card>

            {/* By Technician */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><User className="w-4 h-4" /> Consum per Tehnician</CardTitle></CardHeader>
              <CardContent>
                {data.by_technician.length > 0 ? (
                  <ResponsiveContainer width="100%" height={Math.max(180, data.by_technician.length * 32)}>
                    <BarChart layout="vertical" data={data.by_technician.slice(0, 10)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tick={{ fontSize: 10 }} />
                      <YAxis dataKey="technician_name" type="category" width={120} tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(val) => val.toLocaleString('ro-RO')} />
                      <Bar dataKey="quantity" name="Cantitate" fill="#36B37E" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-slate-400 text-center py-8">Niciun tehnician</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Top Articles Table */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Top Articole Consumate</CardTitle></CardHeader>
            <CardContent>
              {data.top_articles.length === 0 ? (
                <p className="text-slate-400 text-center py-8">Niciun articol consumat in aceasta perioada</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left py-2 px-3">#</th>
                        <th className="text-left py-2 px-3">Articol</th>
                        <th className="text-left py-2 px-3">Cod</th>
                        <th className="text-right py-2 px-3">Cantitate</th>
                        <th className="text-center py-2 px-3">UM</th>
                        <th className="text-right py-2 px-3">Valoare (RON)</th>
                        <th className="text-right py-2 px-3">Tichete</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.top_articles.map((a, i) => (
                        <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                          <td className="py-2 px-3 font-medium text-slate-500">{i + 1}</td>
                          <td className="py-2 px-3 font-medium text-slate-900">{a.article_name}</td>
                          <td className="py-2 px-3 text-slate-500 font-mono text-xs">{a.article_code}</td>
                          <td className="py-2 px-3 text-right font-semibold">{a.quantity.toLocaleString('ro-RO')}</td>
                          <td className="py-2 px-3 text-center text-xs">{a.unit}</td>
                          <td className="py-2 px-3 text-right">{a.value.toLocaleString('ro-RO', { minimumFractionDigits: 2 })}</td>
                          <td className="py-2 px-3 text-right">{a.ticket_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Tickets with Consumption */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Tichete Recente cu Consum</CardTitle></CardHeader>
            <CardContent>
              {data.ticket_details.length === 0 ? (
                <p className="text-slate-400 text-center py-8">Niciun tichet cu materiale consumate</p>
              ) : (
                <div className="space-y-2">
                  {data.ticket_details.slice(0, 15).map((t, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-3 rounded-lg border hover:border-blue-300 cursor-pointer transition-colors"
                      onClick={() => setSelectedTicket(t)}
                      data-testid={`ticket-row-${i}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{t.ticket_title}</p>
                        <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                          <span className="flex items-center gap-1"><Wrench className="w-3 h-3" /> {t.equipment_name}</span>
                          <span className="flex items-center gap-1"><User className="w-3 h-3" /> {t.closed_by_name}</span>
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {t.closed_at?.split('T')[0]}</span>
                        </div>
                      </div>
                      <div className="text-right shrink-0 ml-4">
                        <p className="font-bold text-sm text-blue-600">{t.items_count} articole</p>
                        <p className="text-xs text-slate-500">{t.total_quantity} buc | {t.total_value.toFixed(2)} RON</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
        </TabsContent>

        <TabsContent value="comparison" className="space-y-6 mt-4">
          {loadingComparison ? (
            <div className="flex items-center justify-center h-48">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : comparisonData && (
            <>
              {/* Comparison Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className={comparisonData.changes.quantity_change_pct > 0 ? 'border-red-200' : 'border-green-200'}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Cantitate</p>
                        <p className="text-2xl font-bold">{comparisonData.current_period.total_quantity}</p>
                        <p className="text-xs text-slate-400">vs {comparisonData.previous_period.total_quantity} luna trecuta</p>
                      </div>
                      <div className={`flex items-center gap-1 text-lg font-bold ${comparisonData.changes.quantity_change_pct > 0 ? 'text-red-600' : comparisonData.changes.quantity_change_pct < 0 ? 'text-green-600' : 'text-slate-400'}`}>
                        {comparisonData.changes.quantity_change_pct > 0 ? <ArrowUpRight className="w-5 h-5" /> : comparisonData.changes.quantity_change_pct < 0 ? <ArrowDownRight className="w-5 h-5" /> : <Minus className="w-5 h-5" />}
                        {Math.abs(comparisonData.changes.quantity_change_pct)}%
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className={comparisonData.changes.value_change_pct > 0 ? 'border-red-200' : 'border-green-200'}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Valoare (RON)</p>
                        <p className="text-2xl font-bold">{comparisonData.current_period.total_value.toLocaleString('ro-RO')}</p>
                        <p className="text-xs text-slate-400">vs {comparisonData.previous_period.total_value.toLocaleString('ro-RO')} luna trecuta</p>
                      </div>
                      <div className={`flex items-center gap-1 text-lg font-bold ${comparisonData.changes.value_change_pct > 0 ? 'text-red-600' : comparisonData.changes.value_change_pct < 0 ? 'text-green-600' : 'text-slate-400'}`}>
                        {comparisonData.changes.value_change_pct > 0 ? <ArrowUpRight className="w-5 h-5" /> : comparisonData.changes.value_change_pct < 0 ? <ArrowDownRight className="w-5 h-5" /> : <Minus className="w-5 h-5" />}
                        {Math.abs(comparisonData.changes.value_change_pct)}%
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className={comparisonData.changes.tickets_change_pct > 0 ? 'border-amber-200' : 'border-blue-200'}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Tichete</p>
                        <p className="text-2xl font-bold">{comparisonData.current_period.total_tickets}</p>
                        <p className="text-xs text-slate-400">vs {comparisonData.previous_period.total_tickets} luna trecuta</p>
                      </div>
                      <div className={`flex items-center gap-1 text-lg font-bold ${comparisonData.changes.tickets_change_pct > 0 ? 'text-amber-600' : comparisonData.changes.tickets_change_pct < 0 ? 'text-blue-600' : 'text-slate-400'}`}>
                        {comparisonData.changes.tickets_change_pct > 0 ? <ArrowUpRight className="w-5 h-5" /> : comparisonData.changes.tickets_change_pct < 0 ? <ArrowDownRight className="w-5 h-5" /> : <Minus className="w-5 h-5" />}
                        {Math.abs(comparisonData.changes.tickets_change_pct)}%
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Period Info */}
              <div className="flex items-center gap-4 text-sm text-slate-500">
                <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> Luna curenta: {comparisonData.current_period.start_date} - {comparisonData.current_period.end_date}</span>
                <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> Luna anterioara: {comparisonData.previous_period.start_date} - {comparisonData.previous_period.end_date}</span>
              </div>

              {/* Biggest Increases */}
              <div className="grid lg:grid-cols-2 gap-6">
                <Card className="border-red-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2 text-red-700">
                      <TrendingUp className="w-4 h-4" />
                      Cele mai mari cresteri
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {comparisonData.biggest_increases.length === 0 ? (
                      <p className="text-slate-400 text-center py-6 text-sm">Nicio crestere semnificativa</p>
                    ) : (
                      <div className="space-y-2">
                        {comparisonData.biggest_increases.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-red-50 border border-red-100">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{item.article_name}</p>
                              <p className="text-xs text-slate-500">{item.article_code}</p>
                            </div>
                            <div className="text-right shrink-0 ml-3">
                              <p className="text-sm font-bold text-red-700">+{item.qty_diff} {item.unit}</p>
                              <p className="text-xs text-slate-500">{item.previous_qty} → {item.current_qty}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="border-green-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2 text-green-700">
                      <TrendingDown className="w-4 h-4" />
                      Cele mai mari scaderi
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {comparisonData.biggest_decreases.length === 0 ? (
                      <p className="text-slate-400 text-center py-6 text-sm">Nicio scadere semnificativa</p>
                    ) : (
                      <div className="space-y-2">
                        {comparisonData.biggest_decreases.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-green-50 border border-green-100">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{item.article_name}</p>
                              <p className="text-xs text-slate-500">{item.article_code}</p>
                            </div>
                            <div className="text-right shrink-0 ml-3">
                              <p className="text-sm font-bold text-green-700">{item.qty_diff} {item.unit}</p>
                              <p className="text-xs text-slate-500">{item.previous_qty} → {item.current_qty}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Comparison Bar Chart */}
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">Comparatie Top Articole</CardTitle></CardHeader>
                <CardContent>
                  {(() => {
                    const chartData = comparisonData.current_period.articles.slice(0, 8).map(curr => {
                      const prev = comparisonData.previous_period.articles.find(p => p.article_id === curr.article_id);
                      return {
                        name: curr.article_name.length > 15 ? curr.article_name.substring(0, 15) + '...' : curr.article_name,
                        'Luna curenta': curr.quantity,
                        'Luna anterioara': prev?.quantity || 0
                      };
                    });
                    return chartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-15} textAnchor="end" height={60} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="Luna anterioara" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="Luna curenta" fill="#0052CC" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-slate-400 text-center py-12">Niciun articol de comparat</p>
                    );
                  })()}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Ticket Detail Dialog */}
      <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Detalii Consum Tichet
            </DialogTitle>
          </DialogHeader>
          {selectedTicket && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-semibold text-slate-900">{selectedTicket.ticket_title}</p>
                <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                  <span>{selectedTicket.equipment_name}</span>
                  <span>{selectedTicket.closed_by_name}</span>
                  <span>{selectedTicket.closed_at?.split('T')[0]}</span>
                </div>
              </div>
              <div className="space-y-2">
                <p className="font-medium text-sm">Materiale consumate ({selectedTicket.items_count})</p>
                <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
                  {selectedTicket.items.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2.5">
                      <div>
                        <p className="text-sm font-medium">{item.article_name}</p>
                        <p className="text-xs text-slate-500">{item.article_code}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-sm">{item.quantity} {item.unit}</p>
                        {item.value > 0 && <p className="text-xs text-slate-500">{item.value.toFixed(2)} RON</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                <span className="font-medium text-blue-800">Total</span>
                <span className="font-bold text-blue-900">{selectedTicket.total_quantity} buc | {selectedTicket.total_value.toFixed(2)} RON</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TicketConsumptionPage;
